import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPatreon, faGithub, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faBrain, faTerminal, faDollarSign, faPenNib } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { trackClick, trackEvent } from '../utils/analytics';
import {
  MinimalGlobalStyle,
  Container,
  Inner,
  BackButton,
  Header,
  HeroRow,
  Avatar,
  HeroText,
  PageTitle,
  Bio,
  HeaderLinks,
  HeaderLink,
  Section,
  SectionTitle,
  Card,
  CardTop,
  CardTitle,
  Badge,
  CardDescription,
  CardLinks,
  CardLink,
  InternalLink,
  Tag,
  TagRow,
  Paragraph,
} from '../components/minimal';
import { consciousnessPaper } from '../data/research';

const LinkCard = styled.a`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:first-of-type {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  &:hover {
    border-bottom-color: var(--accent-25);

    h3 {
      color: var(--accent);
    }
  }
`;

const IconTile = styled.div<{ $color: string; $bg: string }>`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.25rem;
  box-shadow:
    inset 0 0 0 1px ${p => p.$color}22,
    0 0 24px ${p => p.$color}22;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const LinkBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const LinkTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.25rem;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const LinkHandle = styled.span`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.45);
  font-variant-numeric: tabular-nums;
  font-weight: 400;
`;

const LinkSub = styled.p`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
  line-height: 1.5;
`;

interface SupportLink {
  title: string;
  handle: string;
  description: string;
  url: string;
  icon: IconDefinition;
  color: string;
  bg: string;
  tracker: () => void;
}

const NexusPage: React.FC = () => {
  const navigate = useNavigate();

  const handleDonationClick = (method: string) => {
    trackEvent('donation_click', { donation_method: method });
  };

  const handleExternalClick = (title: string) => {
    trackClick('nexus_link', { title });
  };

  const supportLinks: SupportLink[] = [
    {
      title: 'Patreon',
      handle: 'patreon.com/A5omic',
      description: 'Monthly support — goes straight into the next project.',
      url: 'https://www.patreon.com/c/A5omic',
      icon: faPatreon,
      color: '#ff424d',
      bg: 'rgba(255, 66, 77, 0.1)',
      tracker: () => handleDonationClick('patreon'),
    },
    {
      title: 'Cash App',
      handle: '$A5omic',
      description: 'One-time tip.',
      url: 'https://cash.app/$A5omic',
      icon: faDollarSign,
      color: '#00d632',
      bg: 'rgba(0, 214, 50, 0.1)',
      tracker: () => handleDonationClick('cashapp'),
    },
  ];

  return (
    <>
      <MinimalGlobalStyle />
      <Container>
        <BackButton />
        <Inner>
          <Header>
            <HeroRow>
              <Avatar>
                <img src="/profile.jpg" alt="Thomas Ryan" />
              </Avatar>
              <HeroText>
                <PageTitle>More</PageTitle>
                <Bio>Links.</Bio>
              </HeroText>
            </HeroRow>
            <HeaderLinks>
              <HeaderLink as="button" onClick={() => { trackClick('nexus_to_build'); navigate('/build'); }}>
                &rarr; Build
              </HeaderLink>
              <HeaderLink href="https://github.com/Atomics-hub" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faGithub} style={{ marginRight: '0.4rem' }} />
                GitHub
              </HeaderLink>
              <HeaderLink href="https://x.com/A5omic" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faXTwitter} style={{ marginRight: '0.4rem' }} />
                X
              </HeaderLink>
            </HeaderLinks>
          </Header>

          <Section>
            <SectionTitle>The Work</SectionTitle>
            <LinkCard
              as="button"
              onClick={() => { trackClick('nexus_build_cta'); navigate('/build'); }}
              style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', fontFamily: 'inherit' }}
            >
              <IconTile $color="#22d3ee" $bg="rgba(34, 211, 238, 0.1)">
                <FontAwesomeIcon icon={faTerminal} />
              </IconTile>
              <LinkBody>
                <LinkTitle>
                  Build
                  <LinkHandle>a5omic.com/build</LinkHandle>
                </LinkTitle>
                <LinkSub>
                  The curated home for my best work — kernel, browsers, tools, writeups, and apps.
                </LinkSub>
              </LinkBody>
            </LinkCard>
          </Section>

          <Section>
            <SectionTitle>Writing</SectionTitle>
            <LinkCard
              href="https://tomryan.dev/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleExternalClick('tomryan-dev')}
            >
              <IconTile $color="#f59e0b" $bg="rgba(245, 158, 11, 0.1)">
                <FontAwesomeIcon icon={faPenNib} />
              </IconTile>
              <LinkBody>
                <LinkTitle>
                  tomryan.dev
                  <LinkHandle>Blog</LinkHandle>
                </LinkTitle>
                <LinkSub>
                  Long-form writing — security research, kernel writeups, and notes on building.
                </LinkSub>
              </LinkBody>
            </LinkCard>
          </Section>

          <Section>
            <SectionTitle>Consciousness Research</SectionTitle>
            <Card>
              <CardTop>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: 0 }}>
                  <IconTile $color="#a78bfa" $bg="rgba(167, 139, 250, 0.1)">
                    <FontAwesomeIcon icon={faBrain} />
                  </IconTile>
                  <CardTitle style={{ flex: 1 }}>{consciousnessPaper.title}</CardTitle>
                </div>
                <Badge $variant="published">PAPER</Badge>
              </CardTop>
              <CardDescription>{consciousnessPaper.meta}</CardDescription>
              <Paragraph>{consciousnessPaper.abstract}</Paragraph>
              <TagRow>
                {consciousnessPaper.tags.map(tag => <Tag key={tag}>{tag}</Tag>)}
              </TagRow>
              <CardLinks>
                {consciousnessPaper.links.map(link => (
                  <CardLink
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleExternalClick('consciousness-paper')}
                  >
                    {link.label} &rarr;
                  </CardLink>
                ))}
              </CardLinks>
            </Card>
          </Section>

          <Section>
            <SectionTitle>Support</SectionTitle>
            {supportLinks.map(link => (
              <LinkCard
                key={link.title}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={link.tracker}
              >
                <IconTile $color={link.color} $bg={link.bg}>
                  <FontAwesomeIcon icon={link.icon} />
                </IconTile>
                <LinkBody>
                  <LinkTitle>
                    {link.title}
                    <LinkHandle>{link.handle}</LinkHandle>
                  </LinkTitle>
                  <LinkSub>{link.description}</LinkSub>
                </LinkBody>
              </LinkCard>
            ))}
          </Section>
        </Inner>
      </Container>
    </>
  );
};

export default NexusPage;
